import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  IconButton,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, AttachMoney as MoneyIcon } from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { collection, query, where, getDocs, Timestamp, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Attendance, attendanceStatusLabels, attendanceStatusColors, MonthlyAttendanceSummary } from '../types/attendance';
import { Employee } from '../types/employee';
import { httpsCallable } from 'firebase/functions';

// バック集計用の型
interface StaffBackSummary {
  staffId: string;
  staffName: string;
  totalBackAmount: number;
  orderCount: number;
  totalSalesAmount: number;
}

const Attendances: React.FC = () => {
  const { shop } = useAuth();
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // 月次サマリー関連
  const [openSummaryDialog, setOpenSummaryDialog] = useState(false);
  const [summaryYear, setSummaryYear] = useState<number>(new Date().getFullYear());
  const [summaryMonth, setSummaryMonth] = useState<number>(new Date().getMonth() + 1);
  const [summaryEmployeeId, setSummaryEmployeeId] = useState<string>('');
  const [monthlySummary, setMonthlySummary] = useState<MonthlyAttendanceSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // 編集ダイアログ関連
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState<Attendance | null>(null);
  const [editClockInTime, setEditClockInTime] = useState<string>('');
  const [editClockOutTime, setEditClockOutTime] = useState<string>('');
  const [editBreakMinutes, setEditBreakMinutes] = useState<number>(0);
  const [editNote, setEditNote] = useState<string>('');
  const [saveLoading, setSaveLoading] = useState(false);

  // バック集計ダイアログ関連
  const [backSummaryDialogOpen, setBackSummaryDialogOpen] = useState(false);
  const [backSummaryYear, setBackSummaryYear] = useState<number>(new Date().getFullYear());
  const [backSummaryMonth, setBackSummaryMonth] = useState<number>(new Date().getMonth() + 1);
  const [staffBackSummaries, setStaffBackSummaries] = useState<StaffBackSummary[]>([]);
  const [backSummaryLoading, setBackSummaryLoading] = useState(false);

  // 従業員一覧取得
  useEffect(() => {
    if (!shop?.id) return;

    const fetchEmployees = async () => {
      try {
        const employeesQuery = query(
          collection(db, 'employees'),
          where('shopId', '==', shop.id),
          where('status', '==', 'active')
        );
        const employeesSnapshot = await getDocs(employeesQuery);
        const employeesList = employeesSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            personalInfo: {
              ...data.personalInfo,
              birthDate: data.personalInfo?.birthDate?.toDate ? data.personalInfo.birthDate.toDate() : new Date(),
            },
            employmentInfo: {
              ...data.employmentInfo,
              hireDate: data.employmentInfo?.hireDate?.toDate ? data.employmentInfo.hireDate.toDate() : new Date(),
              graduationDate: data.employmentInfo?.graduationDate?.toDate ? data.employmentInfo.graduationDate.toDate() : undefined,
            },
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
          } as Employee;
        });
        setEmployees(employeesList);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setError('従業員一覧の取得に失敗しました');
      }
    };

    fetchEmployees();
  }, [shop?.id]);

  // 勤怠一覧取得
  useEffect(() => {
    if (!shop?.id || !selectedDate) return;

    const fetchAttendances = async () => {
      setLoading(true);
      setError('');
      try {
        const startDate = new Date(selectedDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);

        let attendancesQuery = query(
          collection(db, 'attendances'),
          where('shopId', '==', shop.id),
          where('workDate', '>=', Timestamp.fromDate(startDate)),
          where('workDate', '<=', Timestamp.fromDate(endDate)),
          orderBy('workDate', 'desc')
        );

        if (selectedEmployeeId) {
          attendancesQuery = query(
            collection(db, 'attendances'),
            where('shopId', '==', shop.id),
            where('employeeId', '==', selectedEmployeeId),
            where('workDate', '>=', Timestamp.fromDate(startDate)),
            where('workDate', '<=', Timestamp.fromDate(endDate)),
            orderBy('workDate', 'desc')
          );
        }

        const attendancesSnapshot = await getDocs(attendancesQuery);
        const attendancesList = attendancesSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            workDate: data.workDate?.toDate ? data.workDate.toDate() : new Date(),
            clockIn: data.clockIn ? {
              ...data.clockIn,
              timestamp: data.clockIn.timestamp?.toDate ? data.clockIn.timestamp.toDate() : new Date(),
            } : undefined,
            clockOut: data.clockOut ? {
              ...data.clockOut,
              timestamp: data.clockOut.timestamp?.toDate ? data.clockOut.timestamp.toDate() : new Date(),
            } : undefined,
            breaks: data.breaks?.map((b: any) => ({
              startTime: b.startTime?.toDate ? b.startTime.toDate() : new Date(),
              endTime: b.endTime?.toDate ? b.endTime.toDate() : undefined,
              breakMinutes: b.breakMinutes,
            })) || [],
            lateApprovedAt: data.lateApprovedAt?.toDate ? data.lateApprovedAt.toDate() : undefined,
            earlyLeaveApprovedAt: data.earlyLeaveApprovedAt?.toDate ? data.earlyLeaveApprovedAt.toDate() : undefined,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
          } as Attendance;
        });
        setAttendances(attendancesList);
      } catch (err) {
        console.error('Error fetching attendances:', err);
        setError('勤怠一覧の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchAttendances();
  }, [shop?.id, selectedDate, selectedEmployeeId]);

  // 月次サマリー取得
  const handleGetMonthlySummary = async () => {
    if (!summaryEmployeeId) {
      setError('従業員を選択してください');
      return;
    }

    setSummaryLoading(true);
    setError('');
    try {
      const calculateMonthlyAttendance = httpsCallable<
        { employeeId: string; year: number; month: number },
        { success: boolean; summary: MonthlyAttendanceSummary }
      >(functions, 'calculateMonthlyAttendance');

      const result = await calculateMonthlyAttendance({
        employeeId: summaryEmployeeId,
        year: summaryYear,
        month: summaryMonth,
      });

      if (result.data.success) {
        setMonthlySummary(result.data.summary);
      }
    } catch (err: any) {
      console.error('Error calculating monthly summary:', err);
      setError('月次集計の取得に失敗しました: ' + (err.message || ''));
    } finally {
      setSummaryLoading(false);
    }
  };

  const getEmployeeName = (employeeId: string): string => {
    const employee = employees.find((e) => e.id === employeeId);
    if (!employee) return '不明';
    return `${employee.personalInfo.lastName} ${employee.personalInfo.firstName}`;
  };

  const formatTime = (date: Date | undefined): string => {
    if (!date) return '-';
    return format(date, 'HH:mm');
  };

  const formatMinutes = (minutes: number | undefined): string => {
    if (minutes === undefined || minutes === 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}時間${mins}分`;
  };

  // 編集ダイアログを開く
  const handleOpenEditDialog = (attendance: Attendance) => {
    setEditingAttendance(attendance);
    setEditClockInTime(attendance.clockIn?.timestamp ? format(attendance.clockIn.timestamp, 'HH:mm') : '');
    setEditClockOutTime(attendance.clockOut?.timestamp ? format(attendance.clockOut.timestamp, 'HH:mm') : '');
    setEditBreakMinutes(attendance.breakMinutes || 0);
    setEditNote(attendance.note || '');
    setEditDialogOpen(true);
  };

  // 勤怠データを保存
  const handleSaveAttendance = async () => {
    if (!editingAttendance) return;

    setSaveLoading(true);
    setError('');
    try {
      const workDate = new Date(editingAttendance.workDate);
      workDate.setHours(0, 0, 0, 0);

      // 出勤時刻
      let clockInTimestamp: Date | undefined;
      if (editClockInTime) {
        const [hours, minutes] = editClockInTime.split(':').map(Number);
        clockInTimestamp = new Date(workDate);
        clockInTimestamp.setHours(hours, minutes, 0, 0);
      }

      // 退勤時刻
      let clockOutTimestamp: Date | undefined;
      if (editClockOutTime) {
        const [hours, minutes] = editClockOutTime.split(':').map(Number);
        clockOutTimestamp = new Date(workDate);
        clockOutTimestamp.setHours(hours, minutes, 0, 0);

        // 退勤が翌日の場合（24時を超える場合）
        if (clockInTimestamp && clockOutTimestamp < clockInTimestamp) {
          clockOutTimestamp.setDate(clockOutTimestamp.getDate() + 1);
        }
      }

      // 勤務時間・実働時間を計算
      let workMinutes = 0;
      let actualWorkMinutes = 0;
      if (clockInTimestamp && clockOutTimestamp) {
        workMinutes = Math.floor((clockOutTimestamp.getTime() - clockInTimestamp.getTime()) / (1000 * 60));
        actualWorkMinutes = workMinutes - editBreakMinutes;
      }

      // ステータス判定
      let status: 'present' | 'absent' | 'working' = 'present';
      if (clockInTimestamp && !clockOutTimestamp) {
        status = 'working';
      }

      const updateData: any = {
        breakMinutes: editBreakMinutes,
        workMinutes,
        actualWorkMinutes,
        status,
        note: editNote,
        updatedAt: Timestamp.now(),
      };

      if (clockInTimestamp) {
        updateData.clockIn = {
          timestamp: Timestamp.fromDate(clockInTimestamp),
          location: editingAttendance.clockIn?.location || { latitude: 0, longitude: 0 },
          method: 'manual_correction',
        };
      }

      if (clockOutTimestamp) {
        updateData.clockOut = {
          timestamp: Timestamp.fromDate(clockOutTimestamp),
          location: editingAttendance.clockOut?.location || { latitude: 0, longitude: 0 },
          method: 'manual_correction',
        };
      }

      const attendanceRef = doc(db, 'attendances', editingAttendance.id);
      await updateDoc(attendanceRef, updateData);

      // 一覧を再取得
      const startDate = new Date(selectedDate!);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(selectedDate!);
      endDate.setHours(23, 59, 59, 999);

      let attendancesQuery = query(
        collection(db, 'attendances'),
        where('shopId', '==', shop!.id),
        where('workDate', '>=', Timestamp.fromDate(startDate)),
        where('workDate', '<=', Timestamp.fromDate(endDate)),
        orderBy('workDate', 'desc')
      );

      if (selectedEmployeeId) {
        attendancesQuery = query(
          collection(db, 'attendances'),
          where('shopId', '==', shop!.id),
          where('employeeId', '==', selectedEmployeeId),
          where('workDate', '>=', Timestamp.fromDate(startDate)),
          where('workDate', '<=', Timestamp.fromDate(endDate)),
          orderBy('workDate', 'desc')
        );
      }

      const attendancesSnapshot = await getDocs(attendancesQuery);
      const attendancesList = attendancesSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          workDate: data.workDate?.toDate ? data.workDate.toDate() : new Date(),
          clockIn: data.clockIn ? {
            ...data.clockIn,
            timestamp: data.clockIn.timestamp?.toDate ? data.clockIn.timestamp.toDate() : new Date(),
          } : undefined,
          clockOut: data.clockOut ? {
            ...data.clockOut,
            timestamp: data.clockOut.timestamp?.toDate ? data.clockOut.timestamp.toDate() : new Date(),
          } : undefined,
          breaks: data.breaks?.map((b: any) => ({
            startTime: b.startTime?.toDate ? b.startTime.toDate() : new Date(),
            endTime: b.endTime?.toDate ? b.endTime.toDate() : undefined,
            breakMinutes: b.breakMinutes,
          })) || [],
          lateApprovedAt: data.lateApprovedAt?.toDate ? data.lateApprovedAt.toDate() : undefined,
          earlyLeaveApprovedAt: data.earlyLeaveApprovedAt?.toDate ? data.earlyLeaveApprovedAt.toDate() : undefined,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
        } as Attendance;
      });
      setAttendances(attendancesList);

      setEditDialogOpen(false);
      setEditingAttendance(null);
    } catch (err: any) {
      console.error('Error updating attendance:', err);
      setError('勤怠データの更新に失敗しました: ' + (err.message || ''));
    } finally {
      setSaveLoading(false);
    }
  };

  // 勤怠データを削除
  const handleDeleteAttendance = async (attendanceId: string) => {
    if (!window.confirm('この勤怠データを削除してもよろしいですか？')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'attendances', attendanceId));
      setAttendances((prev) => prev.filter((a) => a.id !== attendanceId));
    } catch (err: any) {
      console.error('Error deleting attendance:', err);
      setError('勤怠データの削除に失敗しました: ' + (err.message || ''));
    }
  };

  // バック金額集計を取得
  const handleGetBackSummary = async () => {
    if (!shop?.id) return;

    setBackSummaryLoading(true);
    setError('');
    try {
      // 月の開始と終了を計算
      const monthStart = startOfMonth(new Date(backSummaryYear, backSummaryMonth - 1, 1));
      const monthEnd = endOfMonth(new Date(backSummaryYear, backSummaryMonth - 1, 1));

      // 注文データを取得（salesStaffを含む）
      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(
        ordersRef,
        where('shopId', '==', shop.id),
        where('orderedAt', '>=', Timestamp.fromDate(startOfDay(monthStart))),
        where('orderedAt', '<=', Timestamp.fromDate(endOfDay(monthEnd)))
      );
      const ordersSnapshot = await getDocs(ordersQuery);

      // スタッフ別集計マップ
      const staffMap = new Map<string, StaffBackSummary>();

      ordersSnapshot.docs.forEach((orderDoc) => {
        const orderData = orderDoc.data();
        // キャンセル済み注文は除外
        if (orderData.status === 'cancelled') return;

        const salesStaff = orderData.salesStaff as Array<{
          staffId: string;
          staffName: string;
          shareRate: number;
          backRate?: number;
          backAmount?: number;
        }> | undefined;

        if (!salesStaff || salesStaff.length === 0) return;

        const orderTotal = orderData.total || 0;

        salesStaff.forEach((staff) => {
          const existing = staffMap.get(staff.staffId) || {
            staffId: staff.staffId,
            staffName: staff.staffName,
            totalBackAmount: 0,
            orderCount: 0,
            totalSalesAmount: 0,
          };

          // バック金額（保存されていればそれを使用、なければ計算）
          const backAmount = staff.backAmount !== undefined
            ? staff.backAmount
            : Math.round(orderTotal * staff.shareRate * (staff.backRate ?? shop.costBackSettings?.defaultBackRate ?? 0.5));

          staffMap.set(staff.staffId, {
            ...existing,
            totalBackAmount: existing.totalBackAmount + backAmount,
            orderCount: existing.orderCount + 1,
            totalSalesAmount: existing.totalSalesAmount + (orderTotal * staff.shareRate),
          });
        });
      });

      // 配列に変換してソート
      const summaries = Array.from(staffMap.values())
        .sort((a, b) => b.totalBackAmount - a.totalBackAmount);

      setStaffBackSummaries(summaries);
    } catch (err: any) {
      console.error('Error fetching back summary:', err);
      setError('バック集計の取得に失敗しました: ' + (err.message || ''));
    } finally {
      setBackSummaryLoading(false);
    }
  };

  // 金額フォーマット
  const formatCurrency = (amount: number): string => {
    const currency = shop?.currency || 'JPY';
    const symbol = currency === 'THB' ? '฿' : currency === 'USD' ? '$' : '¥';
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  };

  return (
    <Box>
        <Typography variant="h4" gutterBottom>
          勤怠管理
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                type="date"
                label="日付"
                value={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => setSelectedDate(e.target.value ? new Date(e.target.value) : null)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="従業員"
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                fullWidth
              >
                <MenuItem value="">すべて</MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.personalInfo.lastName} {employee.personalInfo.firstName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                onClick={() => setOpenSummaryDialog(true)}
                fullWidth
              >
                月次集計
              </Button>
            </Grid>
            {shop?.costBackSettings?.enabled && (
              <Grid item xs={12} sm={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setBackSummaryDialogOpen(true)}
                  fullWidth
                  startIcon={<MoneyIcon />}
                >
                  バック集計
                </Button>
              </Grid>
            )}
          </Grid>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>従業員</TableCell>
                <TableCell>日付</TableCell>
                <TableCell>出勤</TableCell>
                <TableCell>退勤</TableCell>
                <TableCell>勤務時間</TableCell>
                <TableCell>休憩時間</TableCell>
                <TableCell>実働時間</TableCell>
                <TableCell>遅刻</TableCell>
                <TableCell>早退</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    読み込み中...
                  </TableCell>
                </TableRow>
              ) : attendances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    勤怠データがありません
                  </TableCell>
                </TableRow>
              ) : (
                attendances.map((attendance) => (
                  <TableRow key={attendance.id}>
                    <TableCell>{getEmployeeName(attendance.employeeId)}</TableCell>
                    <TableCell>{format(attendance.workDate, 'yyyy/MM/dd')}</TableCell>
                    <TableCell>{formatTime(attendance.clockIn?.timestamp)}</TableCell>
                    <TableCell>{formatTime(attendance.clockOut?.timestamp)}</TableCell>
                    <TableCell>{formatMinutes(attendance.workMinutes)}</TableCell>
                    <TableCell>{formatMinutes(attendance.breakMinutes)}</TableCell>
                    <TableCell>{formatMinutes(attendance.actualWorkMinutes)}</TableCell>
                    <TableCell>
                      {attendance.isLate ? (
                        <Chip
                          label={`${attendance.lateMinutes}分`}
                          color={attendance.lateApproved ? 'default' : 'error'}
                          size="small"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {attendance.isEarlyLeave ? (
                        <Chip
                          label={`${attendance.earlyLeaveMinutes}分`}
                          color={attendance.earlyLeaveApproved ? 'default' : 'warning'}
                          size="small"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attendanceStatusLabels[attendance.status]}
                        color={attendanceStatusColors[attendance.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditDialog(attendance)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAttendance(attendance.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 編集ダイアログ */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>勤怠データ編集</DialogTitle>
          <DialogContent>
            {editingAttendance && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Alert severity="info">
                  {getEmployeeName(editingAttendance.employeeId)} / {format(editingAttendance.workDate, 'yyyy/MM/dd')}
                </Alert>

                <TextField
                  type="time"
                  label="出勤時刻"
                  value={editClockInTime}
                  onChange={(e) => setEditClockInTime(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="出勤忘れの場合は設定してください"
                />

                <TextField
                  type="time"
                  label="退勤時刻"
                  value={editClockOutTime}
                  onChange={(e) => setEditClockOutTime(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="退勤忘れの場合は設定してください"
                />

                <TextField
                  type="number"
                  label="休憩時間（分）"
                  value={editBreakMinutes}
                  onChange={(e) => setEditBreakMinutes(parseInt(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 0 }}
                />

                <TextField
                  label="備考"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="修正理由などを記入してください"
                />

                <Alert severity="warning">
                  手動で修正した勤怠データは、method が 'manual_correction' として記録されます。
                </Alert>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
            <Button
              onClick={handleSaveAttendance}
              variant="contained"
              disabled={saveLoading}
            >
              {saveLoading ? '保存中...' : '保存'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 月次集計ダイアログ */}
        <Dialog open={openSummaryDialog} onClose={() => setOpenSummaryDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>月次勤怠集計</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    label="従業員"
                    value={summaryEmployeeId}
                    onChange={(e) => setSummaryEmployeeId(e.target.value)}
                    fullWidth
                  >
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.personalInfo.lastName} {employee.personalInfo.firstName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    type="number"
                    label="年"
                    value={summaryYear}
                    onChange={(e) => setSummaryYear(parseInt(e.target.value))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    type="number"
                    label="月"
                    value={summaryMonth}
                    onChange={(e) => setSummaryMonth(parseInt(e.target.value))}
                    fullWidth
                    inputProps={{ min: 1, max: 12 }}
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                onClick={handleGetMonthlySummary}
                disabled={summaryLoading || !summaryEmployeeId}
              >
                {summaryLoading ? '集計中...' : '集計'}
              </Button>

              {monthlySummary && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>
                    集計結果: {summaryYear}年{summaryMonth}月
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        出勤日数
                      </Typography>
                      <Typography variant="h6">{monthlySummary.workDays}日</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        欠勤日数
                      </Typography>
                      <Typography variant="h6">{monthlySummary.absentDays}日</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        総勤務時間
                      </Typography>
                      <Typography variant="h6">{formatMinutes(monthlySummary.totalWorkMinutes)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        総休憩時間
                      </Typography>
                      <Typography variant="h6">{formatMinutes(monthlySummary.totalBreakMinutes)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        総実働時間
                      </Typography>
                      <Typography variant="h6">
                        {formatMinutes(monthlySummary.totalActualWorkMinutes)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        遅刻回数/合計時間
                      </Typography>
                      <Typography variant="h6">
                        {monthlySummary.lateDays}回 / {formatMinutes(monthlySummary.totalLateMinutes)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        早退回数/合計時間
                      </Typography>
                      <Typography variant="h6">
                        {monthlySummary.earlyLeaveDays}回 /{' '}
                        {formatMinutes(monthlySummary.totalEarlyLeaveMinutes)}
                      </Typography>
                    </Grid>
                    {monthlySummary.estimatedSalary !== undefined && (
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          想定給与
                        </Typography>
                        <Typography variant="h6">
                          ¥{monthlySummary.estimatedSalary.toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSummaryDialog(false)}>閉じる</Button>
          </DialogActions>
        </Dialog>

        {/* バック集計ダイアログ */}
        <Dialog open={backSummaryDialogOpen} onClose={() => setBackSummaryDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon color="primary" />
              スタッフ別バック金額集計
            </Box>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    type="number"
                    label="年"
                    value={backSummaryYear}
                    onChange={(e) => setBackSummaryYear(parseInt(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    type="number"
                    label="月"
                    value={backSummaryMonth}
                    onChange={(e) => setBackSummaryMonth(parseInt(e.target.value))}
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 12 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    onClick={handleGetBackSummary}
                    disabled={backSummaryLoading}
                    fullWidth
                    startIcon={backSummaryLoading ? <CircularProgress size={20} /> : null}
                  >
                    {backSummaryLoading ? '集計中...' : '集計する'}
                  </Button>
                </Grid>
              </Grid>

              {staffBackSummaries.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {backSummaryYear}年{backSummaryMonth}月 集計結果
                  </Typography>

                  {/* 合計サマリーカード */}
                  <Card sx={{ bgcolor: 'primary.50', mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            総バック金額
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatCurrency(staffBackSummaries.reduce((sum, s) => sum + s.totalBackAmount, 0))}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            総売上金額
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(staffBackSummaries.reduce((sum, s) => sum + s.totalSalesAmount, 0))}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            対象スタッフ数
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {staffBackSummaries.length}人
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* スタッフ別詳細テーブル */}
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>スタッフ名</TableCell>
                          <TableCell align="right">注文数</TableCell>
                          <TableCell align="right">売上金額（分配後）</TableCell>
                          <TableCell align="right">バック金額</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {staffBackSummaries.map((summary) => (
                          <TableRow key={summary.staffId}>
                            <TableCell>{summary.staffName}</TableCell>
                            <TableCell align="right">{summary.orderCount}件</TableCell>
                            <TableCell align="right">{formatCurrency(summary.totalSalesAmount)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {formatCurrency(summary.totalBackAmount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {staffBackSummaries.length === 0 && !backSummaryLoading && (
                <Alert severity="info">
                  「集計する」ボタンを押して、スタッフ別バック金額を集計してください。
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBackSummaryDialogOpen(false)}>閉じる</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default Attendances;
